import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, ArrowRight, Gauge, Network, ShieldCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const features = [
  { icon: Gauge, title: "Agentic Productivity Index", desc: "Un índice 0-5 que cuantifica la productividad real aumentada por IA de cada candidato." },
  { icon: Network, title: "Mapa agentic del stack", desc: "Visualiza cómo el candidato encadena agentes de entrada, procesamiento, producción, ejecución y control." },
  { icon: FileText, title: "Informes duales", desc: "Informe técnico completo para el consultor e informe ejecutivo orientado al valor para el cliente." },
  { icon: ShieldCheck, title: "Rigor y compliance", desc: "Detección de inconsistencias y riesgos. La decisión final siempre es humana." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      <header className="border-b border-slate-800">
        <div className="mx-auto max-w-7xl px-5 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/talenthackers-logo.png" alt="Talent Hackers" className="h-8 w-auto object-contain" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary border-l border-slate-700 pl-3">Agentic Deputy</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="header-login-btn" className="text-slate-300 hover:text-primary">Entrar</Button></Link>
            <Link to="/register"><Button data-testid="header-register-btn" className="bg-primary text-slate-950 hover:bg-cyan-400">Registrarse</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1750969185331-e03829f72c7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/60 via-[#020617]/85 to-[#020617]" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 text-xs text-primary mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Evaluación de talento aumentado por IA
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight leading-none">
              No solo evaluamos candidatos.<br />
              <span className="text-primary">Evaluamos su capacidad agentic.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl">
              El <span className="text-slate-200">Agentic Candidate Assessment Deputy</span> ayuda a los consultores de Talent Hackers a analizar, validar y puntuar el ecosistema de agentes de IA que utiliza un candidato — midiendo su productividad real, ejecución y ventaja competitiva.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="bg-primary text-slate-950 hover:bg-cyan-400 gap-2">
                  Empezar evaluación <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-login-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                  Acceso consultor
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-xl border border-slate-800 bg-slate-900 p-6 hover:bg-slate-800/60 transition-colors duration-200"
            >
              <f.icon className="text-primary mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8">
        <div className="mx-auto max-w-7xl px-5 md:px-10 text-sm text-slate-600">
          © {new Date().getFullYear()} Talent Hackers · powered by Catenon · Agentic Assessment Deputy
        </div>
      </footer>
    </div>
  );
}
